

import React, { useState, useRef } from 'react';
import MapShell from '../MapShell';
import useUIPresentationMode from '../helpers/useUIPresentationMode';
import Parse from '../../parseConfig';

import StockSideBarLeft from './StockSideBarLeft';
import StockSideBarRight from './StockSideBarRight';
import StockMapComponent from './StockMapComponent';
import StockListingAdminModal from './StockListingAdminModal';

import { fetchStockData } from './fetchStockData';
import { userHasListingEditorRole } from './userHasListingEditorRole';
import { useEffect } from 'react';


export default function StockLayout() {

  const { presentationModeSize, presentationModeiFrame } = useUIPresentationMode();
  const currentUser = Parse.User.current();

  const urlParams = new URLSearchParams(window.location.search);
  const listingTypeSpec = urlParams.get('type');  // Will be 'ff' for Fraemohs First, or null.
  const franchiseURLParam = urlParams.get('f');  // e.g. 'C', 'W', etc


  const openRightSidebarRef = useRef(null);

  // Minimal state we’ll expand later
  const [selectedListing, setSelectedListing] = useState(null);

  const [listings, setListings] = useState([]);
  const [adminListings, setAdminListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isListingAdminOpen, setIsListingAdminOpen] = useState(false);
  const [canManageListings, setCanManageListings] = useState(false);
  const [listingAdminInitialListing, setListingAdminInitialListing] = useState(null);


  const [filters, setFilters] = useState({
    bedrooms: new Set(),
    availability: new Set(),
    progress: new Set(),
    buildSpec: new Set(),
    franchise: new Set(),
  });

  const checkListingEditorRoleMembership = React.useCallback(async () => {
    if (!currentUser) {
      setCanManageListings(false);
      return;
    }

    try {
      const isListingEditor = await userHasListingEditorRole(currentUser);
      setCanManageListings(isListingEditor);
    } catch (error) {
      console.error('Failed to check listing editor role membership:', error);
      setCanManageListings(false);
    }
  }, [currentUser]);


  function filterListingsByUrlParams(listings, listingTypeSpec, franchiseURLParam) {
    let filtered = listings;

    // --- Fraemohs First (buildSpec filter) ---
    if (listingTypeSpec === 'ff') {
      filtered = filtered.filter(
        (l) =>
          typeof l.buildSpec === 'string' &&
          l.buildSpec.trim().toLowerCase() === 'fraemohs first'
      );
    }

    // --- Franchise filter ---
    if (franchiseURLParam) {
      const franchiseMap = {
        C: 'Canterbury',
        W: 'Waikato',
        M: 'Mackenzie',
        B: 'Bay of Plenty',
        T: 'Tasman',
      };

      const fullFranchise = franchiseMap[franchiseURLParam.toUpperCase()];
      if (fullFranchise) {
        filtered = filtered.filter(
          (l) =>
            typeof l.franchise === 'string' &&
            l.franchise.trim().toLowerCase() === fullFranchise.toLowerCase()
        );
      }
    }

    return filtered;
  }

  const applyListings = React.useCallback((data, { resetFilters = false } = {}) => {
    const filteredData = filterListingsByUrlParams(data, listingTypeSpec, franchiseURLParam);
    const activeListings = filteredData.filter((listing) => listing.inactive !== true);

    setAdminListings(filteredData);
    setListings(activeListings);

    if (!resetFilters) {
      return;
    }

    const allBedrooms = new Set();
    const allAvailability = new Set();
    const allProgress = new Set();
    const allBuildSpec = new Set();
    const allFranchise = new Set();

    activeListings.forEach((l) => {
      if (l?.bedrooms !== null && l?.bedrooms !== undefined && l?.bedrooms !== '') {
        const num = Number(l.bedrooms);
        if (!Number.isNaN(num)) allBedrooms.add(num);
      }
      if (l?.availability) allAvailability.add(String(l.availability).trim());
      if (l?.progress) allProgress.add(String(l.progress).trim());
      if (l?.buildSpec) allBuildSpec.add(String(l.buildSpec).trim());
      if (l?.franchise) allFranchise.add(String(l.franchise).trim());
    });

    setFilters({
      bedrooms: allBedrooms,
      availability: allAvailability,
      progress: allProgress,
      buildSpec: allBuildSpec,
      franchise: allFranchise,
    });
  }, [listingTypeSpec, franchiseURLParam]);

  const refreshListings = React.useCallback(async () => {
    try {
      setLoadError(null);
      const data = await fetchStockData({ includeInactive: true });
      applyListings(data);
    } catch (err) {
      console.error('Failed to refresh stock data:', err);
      setLoadError(err);
    }
  }, [applyListings]);


  const listingsFilteredByType = React.useMemo(() => {
    return filterListingsByUrlParams(listings, listingTypeSpec, franchiseURLParam);
  }, [listings, listingTypeSpec, franchiseURLParam]);


  // Derive unique filter options from listings
  const uniqueOptions = React.useMemo(() => {

    const uniqueBedrooms = new Set();
    const uniqueAvailability = new Set();
    const uniqueProgress = new Set();
    const uniqueBuildSpec = new Set();
    const uniqueFranchise = new Set();

    listingsFilteredByType.forEach((l) => {
      if (l?.bedrooms !== null && l?.bedrooms !== undefined && l?.bedrooms !== '') {
        const num = Number(l.bedrooms);
        if (!Number.isNaN(num)) uniqueBedrooms.add(num);
      }
      if (l?.availability) uniqueAvailability.add(String(l.availability).trim());
      if (l?.progress) uniqueProgress.add(String(l.progress).trim());
      if (l?.buildSpec) uniqueBuildSpec.add(String(l.buildSpec).trim());
      if (l?.franchise) uniqueFranchise.add(String(l.franchise).trim());
    });

    // Convert to arrays for rendering
    return {
      bedrooms: Array.from(uniqueBedrooms).sort((a, b) => a - b),
      availability: Array.from(uniqueAvailability).sort(),
      progress: Array.from(uniqueProgress).sort(),
      buildSpec: Array.from(uniqueBuildSpec).sort(),       // NEW
      franchise: Array.from(uniqueFranchise).sort(),
    };
  }, [listingsFilteredByType]);


  const filteredListings = React.useMemo(() => {

    return listingsFilteredByType.filter((l) => {
      // Bedrooms
      const totalBedrooms = uniqueOptions.bedrooms.length;
      const allBedroomsSelected = filters.bedrooms.size === totalBedrooms;
      const bedroomsOk =
        totalBedrooms === 0 || // if globally empty, always passes
        l.bedrooms == null ||  // 👈 allow listings with no bedroom info to always pass
        allBedroomsSelected ||
        filters.bedrooms.has(Number(l.bedrooms));

      // Availability
      const totalAvail = uniqueOptions.availability.length;
      const allAvailSelected = filters.availability.size === totalAvail;
      const availValue =
        typeof l.availability === 'string' ? l.availability.trim() : l.availability;
      const availabilityOk =
        totalAvail === 0 ||
        availValue == null ||
        availValue === '' || // ✅ allow missing
        allAvailSelected ||
        filters.availability.has(String(availValue));

      // Progress
      const totalProg = uniqueOptions.progress.length;
      const allProgSelected = filters.progress.size === totalProg;
      const progValue =
        typeof l.progress === 'string' ? l.progress.trim() : l.progress;
      const progressOk =
        totalProg === 0 ||
        progValue == null ||
        progValue === '' || // ✅ allow missing
        allProgSelected ||
        filters.progress.has(String(progValue));

      // BuildSpec
      const totalBuildSpec = uniqueOptions.buildSpec.length;
      const allBuildSpecSelected = filters.buildSpec.size === totalBuildSpec;
      const buildSpecValue =
        typeof l.buildSpec === 'string' ? l.buildSpec.trim() : l.buildSpec;
      const buildSpecOk =
        totalBuildSpec === 0 ||
        buildSpecValue == null ||
        buildSpecValue === '' || // ✅ allow missing
        allBuildSpecSelected ||
        filters.buildSpec.has(String(buildSpecValue));

      // Franchise
      const totalFranchise = uniqueOptions.franchise.length;
      const allFranchiseSelected = filters.franchise.size === totalFranchise;
      const franchiseValue =
        typeof l.franchise === 'string' ? l.franchise.trim() : l.franchise;
      const franchiseOk =
        totalFranchise === 0 ||
        franchiseValue == null ||
        franchiseValue === '' || // ✅ allow missing
        allFranchiseSelected ||
        filters.franchise.has(String(franchiseValue));

      return bedroomsOk && availabilityOk && progressOk && buildSpecOk && franchiseOk;
    });

  }, [listingsFilteredByType, filters, uniqueOptions]);


  // When the user clears filters, we set a flag. Once filteredListings updates,
  // zoom to all using the NEW listings, then clear the flag.
  React.useEffect(() => {
    if (pendingZoomAfterClearRef.current) {
      // wait one frame so markers effect runs
      requestAnimationFrame(() => {
        mapRef.current?.zoomToAll?.();
        pendingZoomAfterClearRef.current = false;
      });
    }
  }, [filteredListings]);


  React.useEffect(() => {
    const nextSelectedListing = filteredListings.find((l) => l.id === selectedListing?.id);

    if (!selectedListing) {
      return;
    }

    if (nextSelectedListing) {
      if (nextSelectedListing !== selectedListing) {
        setSelectedListing(nextSelectedListing);
      }
      return;
    }

    if (selectedListing && !filteredListings.some((l) => l.id === selectedListing.id)) {
      setSelectedListing(null);
      mapRef.current?.clearMarkerSelection();
    }
  }, [filteredListings, selectedListing]);


  const mapRef = useRef(null);
  const pendingZoomAfterClearRef = React.useRef(false);


  useEffect(() => {
    checkListingEditorRoleMembership();
  }, [checkListingEditorRoleMembership]);

  useEffect(() => {
    const recheckRoleMembership = () => {
      checkListingEditorRoleMembership();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recheckRoleMembership();
      }
    };

    window.addEventListener('focus', recheckRoleMembership);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', recheckRoleMembership);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkListingEditorRoleMembership]);

  useEffect(() => {

    let isCancelled = false;

    async function load() {

      setIsLoading(true);
      setLoadError(null);

      try {

        const data = await fetchStockData({ includeInactive: true });

        if (!isCancelled) {
          applyListings(data, { resetFilters: true });
        }

      } catch (err) {
        console.error('Failed to load stock data:', err);
        if (!isCancelled) setLoadError(err);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      isCancelled = true;
    };
  }, [applyListings]);


  return (
    <>
      <MapShell
        presentationModeSize={presentationModeSize}
        onRequestRightSidebarOpen={(fn) => {
          //console.log('[MainLayout] MapShell provided openRightSidebar fn');
          openRightSidebarRef.current = fn;
        }}
        leftContent={
          <StockSideBarLeft
            listingTypeSpec={listingTypeSpec}
            franchiseURLParam={franchiseURLParam}
            isVisible={presentationModeSize > 1}
            selectedListing={selectedListing}
            listings={filteredListings}
            isLoading={isLoading}
            loadError={loadError}
            showListingAdminButton={canManageListings}
            onOpenListingAdmin={() => {
              setListingAdminInitialListing(null);
              setIsListingAdminOpen(true);
            }}
            uniqueOptions={uniqueOptions}
            filters={filters}
            setFilters={setFilters}
            onClearAndZoom={() => { pendingZoomAfterClearRef.current = true; }}
          />
        }
        mapContent={
          <StockMapComponent
            ref={mapRef}
            presentationModeiFrame={presentationModeiFrame}
            selectedListing={selectedListing}
            setSelectedListing={setSelectedListing}
            listings={filteredListings}
            listingCount={filteredListings.length}
            onForceRightSidebarOpen={() => {
              //console.log('[MainLayout] onForceRightSidebarOpen called. Do we have fn?', !!openRightSidebarRef.current);
              openRightSidebarRef.current?.();
            }}
          />
        }
        rightContent={
          <StockSideBarRight
            selectedListing={selectedListing}
            showEditListingButton={canManageListings}
            onEditListing={() => {
              if (!selectedListing) {
                return;
              }

              setListingAdminInitialListing(selectedListing);
              setIsListingAdminOpen(true);
            }}
            clearSelection={() => {
              setSelectedListing(null);
              mapRef.current?.clearMarkerSelection(); // reset icon on map
            }}
          />
        }
      />

      <StockListingAdminModal
        isOpen={isListingAdminOpen}
        listings={adminListings}
        initialListing={listingAdminInitialListing}
        hideListPanel={Boolean(listingAdminInitialListing)}
        onClose={() => {
          setIsListingAdminOpen(false);
          setListingAdminInitialListing(null);
        }}
        onListingsChanged={refreshListings}
      />
    </>
  );
}
